// Meta de la home nueva por idioma (title ≤ 60 caracteres, description ≤ 160).
// La versión española es la que se prerenderiza para los buscadores (scripts/seo-prerender.ts).
export const HOME_META: Record<string, { t: string; d: string }> = {
  es: {
    t: "Software agrícola para invernaderos en Almería | Ulture",
    d: "Tu cuadrilla, tus tratamientos y el coste de cada nave en el móvil con Ulture Tactic y Ulture Pest. Hecho por agricultores de Aguadulce (Almería).",
  },
  en: {
    t: "Ulture · Greenhouse farm management software: Tactic & Pest",
    d: "Run your team, crop treatments and production from your phone with Ulture Tactic and Ulture Pest. Farm software built by growers in Almería, Spain.",
  },
  it: {
    t: "Ulture · Software agricolo per serre: Tactic e Pest",
    d: "Gestisci squadra, trattamenti fitosanitari e produzione dallo smartphone con Ulture Tactic e Ulture Pest. Software agricolo creato da agricoltori di Almería.",
  },
  fr: {
    t: "Ulture · Logiciel agricole pour serres : Tactic et Pest",
    d: "Pilotez votre équipe, vos traitements et votre production depuis votre téléphone avec Ulture Tactic et Ulture Pest, conçus par des agriculteurs d’Almería.",
  },
  nl: {
    t: "Ulture · Software voor kassen en telers: Tactic en Pest",
    d: "Beheer je team, gewasbescherming en productie vanaf je telefoon met Ulture Tactic en Ulture Pest. Teeltsoftware gemaakt door telers uit Almería.",
  },
  ar: {
    t: "Ulture · برنامج إدارة البيوت المحمية: Tactic و Pest",
    d: "نظّم فريقك ومعالجات وقاية النبات وإنتاجك من هاتفك مع Ulture Tactic وUlture Pest، برنامج زراعي صمّمه مزارعون من ألميريا.",
  },
  zh: {
    t: "Ulture · 温室种植管理软件：Tactic 与 Pest",
    d: "用 Ulture Tactic 和 Ulture Pest 在手机上管理团队、植保作业和生产。由西班牙阿尔梅里亚的种植者打造的农业软件。",
  },
};

export const OG_IMAGE = "https://ulture.co/landing/inicio/og-home.jpg";

// Módulo de cada app de la home como página propia (HomeInicio `only`; title ≤ 60, description ≤ 160).
// Ojo: las rutas /tactic y /pest ya pintan las páginas nuevas (src/ulture/UltureSite.tsx) y su title/description
// salen de src/ulture/meta.ts, no de aquí.
// h1/sub son el titular y la entradilla que se pintan encima del módulo. La versión española es la
// que se prerenderiza (scripts/seo-prerender.ts). El title es de marca: «control horario» y «control de
// plagas» los trabajan /farm-operations-software y /pest-management-software (que no compitan en Google).
export type AppModule = "tactic" | "pest";
export const MODULE_META: Record<AppModule, Record<string, { t: string; d: string; h1: string; sub: string }>> = {
  tactic: {
    es: {
      t: "Ulture Tactic · App para iPhone, Android y web",
      d: "Asistencia del equipo confirmada por GPS, tareas por línea con tiempos y cajas, rendimiento en kg/hora y coste por nave. Prueba Ulture Tactic 14 días gratis.",
      h1: "Ulture Tactic: control de personal agrícola",
      sub: "Asistencia con GPS, tareas, horas, cajas y coste por nave: tu equipo de campo en el móvil, al minuto.",
    },
    en: {
      t: "Ulture Tactic · App for iPhone, Android and web",
      d: "GPS-confirmed attendance, tasks per row with times and boxes, output in kg/hour and cost per greenhouse. Try Ulture Tactic free for 14 days.",
      h1: "Ulture Tactic: farm workforce management",
      sub: "GPS attendance, tasks, hours, boxes and cost per greenhouse: your field team on your phone, by the minute.",
    },
    it: {
      t: "Ulture Tactic · App per iPhone, Android e web",
      d: "Presenze confermate via GPS, attività per fila con tempi e cassette, resa in kg/ora e costo per serra. Prova Ulture Tactic gratis per 14 giorni.",
      h1: "Ulture Tactic: gestione del personale agricolo",
      sub: "Presenze via GPS, attività, ore, cassette e costo per serra: la tua squadra in campo sul telefono, al minuto.",
    },
    fr: {
      t: "Ulture Tactic · Appli pour iPhone, Android et web",
      d: "Présence confirmée par GPS, tâches par rang avec temps et caisses, rendement en kg/heure et coût par serre. Essayez Ulture Tactic gratuitement 14 jours.",
      h1: "Ulture Tactic : gestion des équipes agricoles",
      sub: "Présence par GPS, tâches, heures, caisses et coût par serre : votre équipe de terrain sur votre téléphone, à la minute.",
    },
    nl: {
      t: "Ulture Tactic · App voor iPhone, Android en web",
      d: "Aanwezigheid bevestigd via gps, taken per rij met tijden en kisten, productie in kg/uur en kosten per kas. Probeer Ulture Tactic 14 dagen gratis.",
      h1: "Ulture Tactic: personeelsbeheer in de teelt",
      sub: "Aanwezigheid via gps, taken, uren, kisten en kosten per kas: je ploeg op je telefoon, tot op de minuut.",
    },
    ar: {
      t: "Ulture Tactic · تطبيق لأجهزة آيفون وأندرويد والويب",
      d: "حضور الفريق مؤكد عبر GPS، ومهام لكل خط مع الأوقات والصناديق، والإنتاجية بالكيلوغرام في الساعة، والتكلفة لكل بيت محمي. جرّب Ulture Tactic مجانًا لمدة 14 يومًا.",
      h1: "Ulture Tactic: إدارة العمالة الزراعية",
      sub: "الحضور عبر GPS، والمهام، والساعات، والصناديق، والتكلفة لكل بيت محمي: فريقك الميداني على هاتفك، لحظة بلحظة.",
    },
    zh: {
      t: "Ulture Tactic · 适用于 iPhone、Android 和网页的应用",
      d: "GPS 确认出勤，按行记录任务、用时和箱数，以公斤/小时计算效率，并核算每个温室的成本。免费试用 Ulture Tactic 14 天。",
      h1: "Ulture Tactic：农业用工管理",
      sub: "GPS 出勤、任务、工时、箱数和每个温室的成本：田间团队尽在手机，精确到分钟。",
    },
  },
  pest: {
    es: {
      t: "Ulture Pest · App para iPhone, Android y web",
      d: "Muestreo de plagas por nave, registro de tratamientos fitosanitarios, calendario con avisos e inventario, por voz. Prueba Ulture Pest 14 días gratis.",
      h1: "Ulture Pest: control de plagas y tratamientos",
      sub: "Muestreo de plagas, registro de tratamientos fitosanitarios, calendario con avisos e inventario, por voz y desde el móvil.",
    },
    en: {
      t: "Ulture Pest · App for iPhone, Android and web",
      d: "Pest scouting per greenhouse, treatment records, a calendar with reminders and inventory, all by voice. Try Ulture Pest free for 14 days.",
      h1: "Ulture Pest: pest and treatment management",
      sub: "Pest scouting, plant protection treatment records, a calendar with reminders and inventory, by voice and from your phone.",
    },
    it: {
      t: "Ulture Pest · App per iPhone, Android e web",
      d: "Monitoraggio dei parassiti per serra, registro dei trattamenti, calendario con avvisi e magazzino, a voce. Prova Ulture Pest gratis per 14 giorni.",
      h1: "Ulture Pest: controllo di parassiti e trattamenti",
      sub: "Monitoraggio dei parassiti, registro dei trattamenti fitosanitari, calendario con avvisi e magazzino, a voce e dal telefono.",
    },
    fr: {
      t: "Ulture Pest · Appli pour iPhone, Android et web",
      d: "Suivi des ravageurs par serre, registre des traitements, calendrier avec rappels et stock, à la voix. Essayez Ulture Pest gratuitement 14 jours.",
      h1: "Ulture Pest : suivi des ravageurs et des traitements",
      sub: "Suivi des ravageurs, registre des traitements phytosanitaires, calendrier avec rappels et stock, à la voix et depuis votre téléphone.",
    },
    nl: {
      t: "Ulture Pest · App voor iPhone, Android en web",
      d: "Plaagmonitoring per kas, registratie van behandelingen, een kalender met meldingen en voorraad, met je stem. Probeer Ulture Pest 14 dagen gratis.",
      h1: "Ulture Pest: plagen en behandelingen",
      sub: "Plaagmonitoring, registratie van gewasbeschermingsbehandelingen, een kalender met meldingen en voorraad, met je stem en vanaf je telefoon.",
    },
    ar: {
      t: "Ulture Pest · تطبيق لأجهزة آيفون وأندرويد والويب",
      d: "رصد الآفات لكل بيت محمي، وسجل معالجات وقاية النبات، وتقويم مع تنبيهات، ومخزون مضبوط تلقائيًا، بالصوت. جرّب Ulture Pest مجانًا لمدة 14 يومًا.",
      h1: "Ulture Pest: مكافحة الآفات والمعالجات",
      sub: "رصد الآفات، وسجل معالجات وقاية النبات، وتقويم مع تنبيهات، والمخزون، بالصوت ومن هاتفك.",
    },
    zh: {
      t: "Ulture Pest · 适用于 iPhone、Android 和网页的应用",
      d: "按温室巡查病虫害，记录植保用药，带提醒的用药日历和自动对账的库存，支持语音操作。免费试用 Ulture Pest 14 天。",
      h1: "Ulture Pest：病虫害与植保管理",
      sub: "病虫害巡查、植保用药记录、带提醒的用药日历和库存管理，可语音操作，手机即可完成。",
    },
  },
};

// «Ver más» del módulo en su propia página: baja al detalle de la app (ModuleDetails, #detalle).
// SOLUTION_PAGE: la página de soluciones de cada app (el HTML para buscadores la enlaza).
export const MODULE_MORE: Record<AppModule, string> = { tactic: "#detalle", pest: "#detalle" };
export const SOLUTION_PAGE: Record<AppModule, string> = {
  tactic: "/farm-operations-software",
  pest: "/pest-management-software",
};
